import { toast } from 'react-toastify';
import { CreateDosageBody, Dosage, UpdateDosageBody } from '@core/models/dosage.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import DosageService from 'services/api/dosage.service';

// -------------------------------------------------------------------------

interface IDosageState {
    loading: boolean;
    error: IErrorModel | undefined;
    dosages: Dosage[];
    dosage: Dosage | null;
}

const initialState: IDosageState = {
    loading: false,
    error: {} as IErrorModel,
    dosages: [],
    dosage: null,
};

// -------------------------------------------------------------------------

export const getDosagesThunk = createAsyncThunk<Dosage[], void, { rejectValue: IErrorModel }>(
    `/dosage/getDosages`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await DosageService.getDosages();
            return response.data.dosages
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch dosages', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const getDosageByIdThunk = createAsyncThunk<Dosage, number, { rejectValue: IErrorModel }>(
    `/dosage/getDosageById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await DosageService.getDosageById(id);
            return response.data.dosage
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch dosage', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const createDosageThunk = createAsyncThunk<
    ISuccessResponse,
    CreateDosageBody,
    { rejectValue: IErrorModel }
>(`/dosage/createDosage`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await DosageService.createDosage(body);
        toast.success(response.data.message || 'Dosage created successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to create dosage', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const updateDosageThunk = createAsyncThunk<
    ISuccessResponse,
    UpdateDosageBody,
    { rejectValue: IErrorModel }
>(`/dosage/updateDosage`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await DosageService.updateDosage(body);
        toast.success(response.data.message || 'Dosage updated successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to update dosage', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const deleteDosageThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/dosage/deleteDosage`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await DosageService.deleteDosage(id);
            toast.success(response.data.message || 'Dosage deleted successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to delete dosage', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

// -------------------------------------------------------------------------

const dosageSlice = createSlice({
    name: `dosage`,
    initialState,
    reducers: {
        clearDosage(state) {
            state.dosage = null;
        },
    },
    extraReducers: (builder) => {
        // Get Dosages
        builder.addCase(getDosagesThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getDosagesThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.dosages = action.payload;
        });
        builder.addCase(getDosagesThunk.rejected, (state, action) => {
            state.loading = false;
            state.dosages = [];
            state.error = action.payload;
        });

        // Get Dosage By Id
        builder.addCase(getDosageByIdThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getDosageByIdThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.dosage = action.payload;
        });
        builder.addCase(getDosageByIdThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Create Dosage
        builder.addCase(createDosageThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(createDosageThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(createDosageThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Update Dosage
        builder.addCase(updateDosageThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(updateDosageThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(updateDosageThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Delete Dosage
        builder.addCase(deleteDosageThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(deleteDosageThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(deleteDosageThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default dosageSlice;
export const { clearDosage } = dosageSlice.actions;
export const dosageReducer = dosageSlice.reducer;

